import { cloneElement, useEffect, useState } from "react";
import { Drawer, DrawerContent, DrawerTitle, DrawerDescription } from "@/components/ui/drawer";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { ChevronRight } from "lucide-react";

export function useMobile() {
  const q = "(max-width: 767px)";
  const [m, setM] = useState(() => window.matchMedia(q).matches);
  useEffect(() => {
    const mq = window.matchMedia(q);
    const f = () => setM(mq.matches);
    mq.addEventListener("change", f);
    return () => { mq.removeEventListener("change", f); };
  }, []);
  return m;
}

// Menu simples (sem submenus): lista no computador, folha de baixo no telemóvel.
export function MenuOrSheet({ trigger, items, title, testid, align = "end" }) {
  const mobile = useMobile();
  const [open, setOpen] = useState(false);
  const list = items.filter(Boolean);
  if (mobile) return <>{cloneElement(trigger, { onClick: () => setOpen(true) })}<ActionSheet open={open} onOpenChange={setOpen} title={title} items={list} testid={testid} /></>;
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent align={align} className="bg-white w-64" data-testid={testid}>
        {list.map((it) => {
          const Icon = it.icon;
          return <DropdownMenuItem key={it.testid || it.label} disabled={it.disabled} onClick={it.onClick} data-testid={it.testid}
            className={it.danger ? "text-red-600 focus:text-red-700 focus:bg-red-50" : ""}>{Icon && <Icon size={14} className="mr-2" />}{it.label}</DropdownMenuItem>;
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// Telemóvel: menus «…» / «Mais» abrem uma folha de baixo (opções de 52 px; «sub» abre uma segunda folha, nunca um submenu lateral).
// items: [{ label, hint, icon: Icon, onClick, testid, danger, disabled, sub: [...] }]
export function ActionSheet({ open, onOpenChange, title, items, testid = "action-sheet" }) {
  const [sub, setSub] = useState(null);
  useEffect(() => { if (!open) { setSub(null); } }, [open]);
  const list = (sub ? sub.sub : items).filter(Boolean);
  const safe = list.filter((x) => !x.danger), danger = list.filter((x) => x.danger);
  const pick = (it) => {
    if (it.disabled) return;
    if (it.sub) { setSub(it); return; }
    onOpenChange(false);
    setTimeout(() => it.onClick?.(), 120);
  };
  const Row = ({ it }) => {
    const Icon = it.icon;
    return (
      <button type="button" onClick={() => pick(it)} disabled={it.disabled} data-testid={it.testid}
        className={`w-full min-h-[52px] flex items-center gap-3 px-5 py-2.5 text-left active:bg-slate-100 disabled:opacity-40 ${it.danger ? "text-red-600" : "text-slate-900"}`}>
        {Icon ? <Icon size={19} className={`shrink-0 ${it.danger ? "" : "text-slate-500"}`} /> : <span className="w-[19px] shrink-0" />}
        <span className="flex-1 min-w-0">
          <span className="block text-[16px] font-medium leading-snug">{it.label}</span>
          {it.hint && <span className="block text-[13px] text-slate-500 leading-snug">{it.hint}</span>}
        </span>
        {it.sub && <ChevronRight size={18} className="text-slate-400 shrink-0" />}
      </button>
    );
  };
  return (
    <Drawer open={open} onOpenChange={onOpenChange} shouldScaleBackground={false}>
      <DrawerContent className="bg-white max-h-[85vh] pb-[env(safe-area-inset-bottom)]" data-testid={testid}>
        <DrawerTitle className="px-5 pt-3 pb-2 text-[15px] font-semibold text-slate-500 truncate">{sub ? sub.label : title}</DrawerTitle>
        <DrawerDescription className="sr-only">Escolha uma opção</DrawerDescription>
        <div className="overflow-y-auto divide-y divide-slate-100 border-t border-slate-100">
          {safe.map((it) => <Row key={it.testid || it.label} it={it} />)}
          {danger.length > 0 && <div className="h-2 bg-slate-50" />}
          {danger.map((it) => <Row key={it.testid || it.label} it={it} />)}
        </div>
        <div className="p-3 border-t border-slate-100">
          <button type="button" onClick={() => (sub ? setSub(null) : onOpenChange(false))} data-testid={`${testid}-cancel`}
            className="w-full h-12 rounded-[14px] bg-slate-100 text-[16px] font-semibold text-slate-700">{sub ? "Voltar" : "Cancelar"}</button>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
